/**
 * Formatos de fecha y hora.
 *
 * LA REGLA QUE ESTE MÓDULO EXISTE PARA SOSTENER: la zona horaria NUNCA se
 * escribe en el código ni se toma del navegador. El sistema opera en la zona
 * que vive en la tabla `configuracion` y viaja hasta aquí de dos formas: en la
 * respuesta de `/api/disponibilidad` (`zona_horaria`), o en
 * `/api/configuracion`. Quien consuma estos formateadores tiene que pasarla; no
 * hay valor por omisión a propósito, porque el respaldo natural —la zona del
 * navegador— es justo el error que se quiere impedir: un alumno consultando
 * desde otra zona vería horas que no son las de la escuela.
 *
 * El IDIOMA sí se fija aquí: es el idioma de la interfaz, no un dato de la
 * institución.
 */

import { useMemo } from 'react'

export const IDIOMA = 'es-MX'

/** `14:00`, en la zona que reporta el sistema. Formato de 24 horas. */
export function useFormatoDeHora(zonaHoraria: string): Intl.DateTimeFormat {
  return useMemo(
    () =>
      new Intl.DateTimeFormat(IDIOMA, {
        timeZone: zonaHoraria,
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }),
    [zonaHoraria],
  )
}

/**
 * `14:03:27`, en la zona que reporta el sistema.
 *
 * Los segundos NO son un adorno: lo usa el tablero del mostrador para decir a
 * qué hora son los datos que se están viendo, y ahí la pregunta es «¿esto está
 * fresco?». Con precisión de minutos, un refresco caído hace un minuto y uno
 * caído hace cincuenta y nueve segundos se ven igual.
 */
export function useFormatoDeReloj(zonaHoraria: string): Intl.DateTimeFormat {
  return useMemo(
    () =>
      new Intl.DateTimeFormat(IDIOMA, {
        timeZone: zonaHoraria,
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hourCycle: 'h23',
      }),
    [zonaHoraria],
  )
}

/**
 * `lunes, 7 de junio de 2027`, en la zona que reporta el sistema.
 *
 * Aquí la zona SÍ importa aunque parezca que solo se muestra un día: el
 * instante `2027-06-08T05:00:00Z` es el 8 de junio en UTC y el 7 en
 * `America/Tijuana`. Formatear con la zona equivocada corre la reservación de
 * día.
 */
export function useFormatoDeDia(zonaHoraria: string): Intl.DateTimeFormat {
  return useMemo(
    () =>
      new Intl.DateTimeFormat(IDIOMA, {
        timeZone: zonaHoraria,
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
      }),
    [zonaHoraria],
  )
}

/**
 * Sube la primera letra sin tocar el resto.
 *
 * `Intl` devuelve el día largo en minúscula («lunes, 7 de junio…»), y
 * `text-transform: capitalize` en CSS también mayúsculizaría las preposiciones
 * («Lunes, 7 De Junio De 2027»).
 */
export function conInicialMayuscula(texto: string): string {
  if (texto === '') return texto
  return texto.charAt(0).toUpperCase() + texto.slice(1)
}
